import type { Color } from '@onefootprint/design-tokens';
import type { Icon } from '@onefootprint/icons';
import { cva, cx, type VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';

export type IconButtonSize = 'default' | 'large' | 'compact' | 'tiny';
type IconButtonColor = Color;

export type IconButtonProps = {
  'aria-label': string;
  icon: Icon;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  className?: string;
  color?: IconButtonColor;
  destructive?: boolean;
} & VariantProps<typeof iconButton> &
  React.ButtonHTMLAttributes<HTMLButtonElement>;

const iconButton = cva(
  [
    'flex items-center justify-center box-border cursor-pointer',
    'transition-[background-color,border-color,color,box-shadow,transform] duration-100 ease-out',
    'active:enabled:scale-pressed-sm motion-reduce:active:enabled:scale-100',
  ],
  {
    variants: {
      size: {
        large: 'size-10',
        default: 'size-8',
        compact: 'size-7',
        tiny: 'size-6',
      },
      rounded: {
        true: 'rounded-full',
      },
      variant: {
        outline: [
          'border border-solid border-tertiary bg-(--button-secondary-bg) shadow-(--button-secondary-shadow)',
          'hover:bg-[image:linear-gradient(var(--button-secondary-hover-bg),var(--button-secondary-hover-bg))] hover:border-primary hover:shadow-(--button-secondary-hover-shadow)',
          'active:bg-(--button-secondary-active-bg) active:border-primary active:shadow-(--button-secondary-active-shadow)',
          'disabled:pointer-events-none disabled:bg-secondary disabled:border-tertiary disabled:shadow-(--button-secondary-disabled-shadow)',
        ],
        ghost: [
          'bg-transparent border-none',
          'hover:bg-hover-control active:bg-hover-control',
          'disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:bg-none data-[disabled=true]:cursor-default data-[disabled=true]:opacity-50',
          'data-[destructive=true]:hover:bg-error data-[destructive=true]:active:bg-error',
        ],
      },
      destructive: {
        true: [
          'hover:bg-error active:bg-error',
          'data-[destructive=true]:border-error data-[destructive=true]:hover:border-error',
        ],
      },
    },
    compoundVariants: [
      { size: 'large', rounded: undefined, class: 'rounded-(--icon-button-border-radius)' },
      { size: 'large', rounded: false, class: 'rounded-(--icon-button-border-radius)' },
      { size: 'default', rounded: undefined, class: 'rounded-(--icon-button-border-radius)' },
      { size: 'default', rounded: false, class: 'rounded-(--icon-button-border-radius)' },
      { size: 'compact', rounded: undefined, class: 'rounded-(--icon-button-border-radius)' },
      { size: 'compact', rounded: false, class: 'rounded-(--icon-button-border-radius)' },
      { size: 'tiny', rounded: undefined, class: 'rounded-(--icon-button-border-radius-tiny)' },
      { size: 'tiny', rounded: false, class: 'rounded-(--icon-button-border-radius-tiny)' },
      // Bordered like a Button, so it takes the Button's shape (a pill under rb) to sit beside one.
      { variant: 'outline', rounded: undefined, class: 'rounded-(--button-border-radius)!' },
      { variant: 'outline', rounded: false, class: 'rounded-(--button-border-radius)!' },
    ],
    defaultVariants: {
      size: 'default',
      variant: 'ghost',
    },
  },
);

const getIconColor = (color?: IconButtonColor, destructive?: boolean, disabled?: boolean) => {
  if (destructive) {
    return 'error';
  }
  if (disabled) {
    return 'quaternary';
  }
  if (color) {
    return color;
  }
  return 'primary';
};

const handleClick = (
  event: React.MouseEvent<HTMLButtonElement>,
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void,
) => {
  event.stopPropagation();
  onClick?.(event);
};

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      'aria-label': ariaLabel,
      icon: Icon,
      onClick,
      size,
      variant,
      rounded,
      disabled,
      className,
      color = 'primary',
      destructive,
      ...props
    }: IconButtonProps,
    ref,
  ) => {
    return (
      <button
        className={cx(iconButton({ size, variant, rounded, destructive }), className)}
        aria-label={ariaLabel}
        onClick={e => handleClick(e, onClick)}
        disabled={disabled}
        ref={ref}
        tabIndex={0}
        type="button"
        data-destructive={destructive}
        {...props}
      >
        <Icon color={getIconColor(color, destructive, disabled)} />
      </button>
    );
  },
);

export default IconButton;
